import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Check, X, Minus, ArrowRight } from 'lucide-react';

const ROWS = [
  { label: 'Cost efficiency', hipro: '40–60% of big-agency rates', frees: 'Cheap, but hidden rework costs', cons: 'Premium rates + overhead' },
  { label: 'Speed to start', hipro: 'Team assembled in 1–2 weeks', frees: 'Fast, if available', cons: '4–12 weeks of onboarding' },
  { label: 'Skill breadth', hipro: 'Design, dev, AI, cloud, QA under one roof', frees: 'One person, one skill', cons: 'Broad, billed per specialist' },
  { label: 'Code & IP ownership', hipro: true, frees: 'Often unclear', cons: 'Sometimes retained' },
  { label: 'SLAs & 24/7 support', hipro: true, frees: false, cons: 'At enterprise price tiers' },
  { label: 'Replacement cover', hipro: 'Bench cover, zero disruption', frees: false, cons: 'Slow internal reshuffle' },
  { label: 'Weekly demos & transparency', hipro: true, frees: 'Varies', cons: 'Status reports, not demos' },
  { label: 'Accountability', hipro: 'Single contract, outcome-owned', frees: 'Individual, best-effort', cons: 'Diffused across layers' },
];

const Cell = ({ v, highlight }) => {
  if (v === true) return <Check className={`h-5 w-5 mx-auto ${highlight ? 'text-emerald-400' : 'text-emerald-500'}`} />;
  if (v === false) return <X className="h-5 w-5 mx-auto text-rose-400" />;
  if (!v) return <Minus className="h-5 w-5 mx-auto text-slate-300" />;
  return <span className={`text-xs sm:text-sm ${highlight ? 'text-white font-semibold' : 'text-slate-600'}`}>{v}</span>;
};

const WhyHipro = () => {
  const nav = useNavigate();
  return (
    <section id="why-hipro" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-orange-600">Why Hipro</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-900">The smart choice between freelancers and big consultancies</h2>
          <p className="mt-4 text-slate-600">Enterprise-grade delivery without enterprise-grade invoices — and none of the freelancer roulette.</p>
        </div>
        <div className="mt-10 overflow-x-auto rounded-2xl border border-slate-200 shadow-sm" data-testid="why-hipro-table">
          <table className="w-full text-center min-w-[720px]">
            <thead>
              <tr className="text-sm">
                <th className="text-left px-5 py-4 bg-slate-50 font-semibold text-slate-500 w-1/4"></th>
                <th className="px-5 py-4 bg-slate-950 text-white font-extrabold relative">
                  <span className="absolute -top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wider bg-orange-500 text-white rounded-full px-3 py-1">Recommended</span>
                  Hipro Technologies
                </th>
                <th className="px-5 py-4 bg-slate-50 font-bold text-slate-700">Freelancers</th>
                <th className="px-5 py-4 bg-slate-50 font-bold text-slate-700">Big Consultancies</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, i) => (
                <tr key={r.label} className={i % 2 ? 'bg-slate-50/50' : 'bg-white'}>
                  <td className="text-left px-5 py-3.5 text-sm font-semibold text-slate-800">{r.label}</td>
                  <td className="px-5 py-3.5 bg-slate-950/95"><Cell v={r.hipro} highlight /></td>
                  <td className="px-5 py-3.5"><Cell v={r.frees} /></td>
                  <td className="px-5 py-3.5"><Cell v={r.cons} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-8 text-center">
          <Button onClick={() => nav('/book-meeting')} data-testid="why-hipro-cta" className="bg-orange-500 hover:bg-orange-600 h-11 px-7 rounded-full">
            Get the best of both worlds <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default WhyHipro;
